import axios from 'axios';

// Criar a instância do axios com configurações aprimoradas
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const baseURL = isLocalhost 
  ? 'http://localhost:5003/api'
  : 'https://backoffice-seminovos-api.easycarros.com/api';

console.log('🔧 API baseURL:', baseURL, '| Hostname:', window.location.hostname);

const api = axios.create({
  baseURL,
  timeout: 20000, // 20 segundos de timeout padrão (aumentado)
  headers: {
    'ngrok-skip-browser-warning': 'true'
  }
});

// Função de retry para requisições falhas
const retryDelay = (retryNumber = 0) => {
  const delays = [1000, 2000, 3000]; // Atrasos progressivos
  return delays[retryNumber] || 4000;
};

// Interceptor para adicionar cabeçalhos anti-cache nas requisições GET
api.interceptors.request.use(config => {
  // Garantir que o cabeçalho ngrok esteja em todas as requisições
  config.headers['ngrok-skip-browser-warning'] = 'true';
  
  // Adicionar token de autenticação se existir
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Configurar Content-Type baseado no tipo de dados
  if (config.data && config.data instanceof FormData) {
    // Para FormData, remover Content-Type para que o navegador defina automaticamente como multipart/form-data
    delete config.headers['Content-Type'];
  } else {
    // Para outros tipos de dados, definir como JSON
    config.headers['Content-Type'] = 'application/json';
  }
  
  // Adicionar cabeçalhos anti-cache apenas para requisições GET
  if (config.method === 'get') {
    config.headers = {
      ...config.headers,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    };
  }
  
  // Configurar retries de forma específica para cada tipo de rota
  if (config.url?.includes('/whatsapp/init')) {
    // Para inicialização do WhatsApp, permitir apenas 1 retry com timeout maior
    config.retry = 1;
    config.timeout = 45000; // 45 segundos para init
  } else if (config.url?.includes('/whatsapp/status') || config.url?.includes('/whatsapp/validate-connection')) {
    // Para verificações de status, permitir mais retries mas com timeout menor
    config.retry = 3; 
    config.timeout = 10000; // 10 segundos para status
  } else if (config.url?.includes('/whatsapp/refresh-session')) {
    // Para refresh de sessão, permitir 1 retry com timeout maior
    config.retry = 1;
    config.timeout = 30000; // 30 segundos para refresh
  } else if (config.url?.includes('/send-bulk')) {
    // Para envios em massa, sem retry
    config.retry = 0;
  } else {
    // Configuração padrão para outras requisições
    config.retry = config.retry || 2; // Tentar até 2 vezes
  }
  
  // Inicializar contador de retries se não existir
  config.retryCount = config.retryCount || 0;
  
  return config;
});

// Interceptor para tratar erros de forma global com retry
api.interceptors.response.use(
  // Primeiro normaliza respostas dos endpoints antes do tratamento de erros/retries
  response => {
    try {
      const url = response.config?.url || '';

      // Normalizar apenas respostas relacionadas a veículos para evitar efeitos colaterais
      if (url.includes('/veiculos')) {
        const normalize = (obj) => {
          if (!obj || typeof obj !== 'object') return obj;

          // Se o backend retornar `multimidia` ou `documentos`, mapear para `midia`/`anexos`
          if (obj.multimidia && !obj.midia) obj.midia = obj.multimidia;
          if (obj.documentos && !obj.anexos) obj.anexos = obj.documentos;

          // Garantir estrutura de arrays
          if (obj.midia && !obj.midia.fotos) obj.midia.fotos = [];
          if (obj.midia && !obj.midia.videos) obj.midia.videos = [];
          if (!obj.anexos) obj.anexos = [];

          return obj;
        };

        if (Array.isArray(response.data)) {
          response.data = response.data.map(normalize);
        } else if (response.data && typeof response.data === 'object') {
          // Alguns endpoints envolvem { data: {...} } ou retorno direto
          if (response.data.data && typeof response.data.data === 'object') {
            if (Array.isArray(response.data.data)) {
              response.data.data = response.data.data.map(normalize);
            } else {
              response.data.data = normalize(response.data.data);
            }
          } else {
            response.data = normalize(response.data);
          }
        }
      }
    } catch (e) {
      // não falhar por causa da normalização
      console.warn('Falha ao normalizar resposta de veiculos:', e);
    }

    return response;
  },
  async error => {
    const config = error.config;
    
    // Se é erro 401, tratar token inválido/expirado
    if (error.response?.status === 401) {
      const errorData = error.response?.data;
      
      // Se é token expirado, mostrar mensagem específica
      if (errorData?.expired || errorData?.message?.includes('expirado')) {
        console.warn('Token expirado - redirecionando para login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Evitar múltiplos redirecionamentos
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?expired=true';
        }
      } else {
        // Token inválido ou não fornecido
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
      
      return Promise.reject(error);
    }
    
    // Se foi abortado pelo usuário ou é um erro específico que não deve ser repetido
    if (!config || error.message === 'canceled' || error.response?.status === 403) {
      return Promise.reject(error);
    }
    
    // Para erros de timeout e conexão, tentar novamente
    if ((error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('Network Error')) 
        && config.retryCount < config.retry) {
      
      // Incrementar contador de tentativas
      config.retryCount += 1;
      
      console.warn(`Tentando novamente (${config.retryCount}/${config.retry}) após falha: ${error.message}`);
      
      // Esperar antes de tentar novamente
      await new Promise(resolve => setTimeout(resolve, retryDelay(config.retryCount)));
      
      // Criar uma nova requisição com as mesmas configurações
      return api(config);
    }
    
    // Para outros erros ou se já excedeu o número de tentativas
    if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
      console.warn('A requisição foi abortada ou expirou o tempo limite');
    }
    
    return Promise.reject(error);
  }
);

// Função para gerar URLs de imagens
export const getImageUrl = (src) => {
  if (!src || src.startsWith('http')) return src;
  
  // Usar a mesma baseURL configurada no axios
  const baseUrl = api.defaults.baseURL.replace('/api', '');
  
  return `${baseUrl}${src}`;
};

export default api;
